import React, { useMemo, useRef, useEffect } from 'react';
import { View, Text, Platform } from 'react-native';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import IndividualDataTable from '@navigation/screens/LowLevel/Components/DataTable';
import moment from 'moment';
import type { UIComponentContract } from '../../types/componentContract';

// Conditional import for react-table (web only)
let useTable: any, useSortBy: any, usePagination: any, useGlobalFilter: any;
if (Platform.OS === 'web') {
  try {
    const reactTable = require('react-table');
    useTable = reactTable.useTable;
    useSortBy = reactTable.useSortBy;
    usePagination = reactTable.usePagination;
    useGlobalFilter = reactTable.useGlobalFilter;
  } catch (e) {
    console.warn('react-table not available:', e);
  }
}

export const uiContract: UIComponentContract = {
  id: "table.data",
  accepts: {
    capability: "display_table",
    shape: {
      data: "array",
      columns: "array",
      pageSize: "number?",
      searchable: "boolean?"
    },
    required: ["data", "columns"],
    optional: ["pageSize", "searchable"]
  },
  renders: "table",
  priority: 10,
  surface: "dashboard"
};

interface DataTableProps {
  data: {
    data: any[];
    columns: Array<{
      Header: string;
      accessor: string;
      Cell?: (props: any) => React.ReactNode;
      sortType?: (rowA: any, rowB: any) => number;
    }>;
    pageSize?: number;
    searchable?: boolean;
    onRowClick?: (row: any) => void;
    // For native fallback
    dataSchema?: Array<{ title: string; key: string; type: string }>;
  };
}

const DataTableComponent: React.FC<DataTableProps> = ({ data: propsData }) => {
  const theme = themeSelector();
  const { data, columns, pageSize = 10, searchable = true, onRowClick, dataSchema } = propsData || {};
  
  // Use refs to store stable references
  const stableDataRef = useRef<any[]>([]);
  const stableColumnsRef = useRef<any[]>([]);
  const dataHashRef = useRef<string>('');
  const columnsHashRef = useRef<string>('');
  
  // Create stable data reference - only update when content actually changes
  const stableData = useMemo(() => {
    const dataArray = (!data || !Array.isArray(data)) ? [] : data;
    const dataHash = JSON.stringify(dataArray);
    
    if (dataHash === dataHashRef.current && stableDataRef.current.length > 0) {
      return stableDataRef.current;
    }
    
    dataHashRef.current = dataHash;
    stableDataRef.current = dataArray;
    return dataArray;
  }, [data]);

  // Create stable columns reference - only update when content actually changes
  const stableColumns = useMemo(() => {
    const columnsArray = (!columns || !Array.isArray(columns)) ? [] : columns;
    const columnsHash = JSON.stringify(columnsArray);
    
    if (columnsHash === columnsHashRef.current && stableColumnsRef.current.length > 0) {
      return stableColumnsRef.current;
    }
    
    columnsHashRef.current = columnsHash;
    stableColumnsRef.current = columnsArray;
    return columnsArray;
  }, [columns]);

  // Memoize table options to prevent recreation
  const tableOptions = useMemo(() => ({
    columns: stableColumns,
    data: stableData,
    initialState: { pageIndex: 0, pageSize },
    manualPagination: false,
    autoResetPage: false,
    autoResetSortBy: false,
    autoResetFilters: false,
    disableSortBy: false,
    disableFilters: false,
  }), [stableColumns, stableData, pageSize]);

  // React Table instance (web only) - must be called at top level
  // Only recreate if data/columns actually changed
  const tableInstance = Platform.OS === 'web' && useTable ? useTable(
    tableOptions,
    useGlobalFilter,
    useSortBy,
    usePagination
  ) : null;

  // Extract table methods and state - use refs to prevent re-creation
  const tableMethodsRef = useRef<any>(null);
  
  if (!tableInstance) {
    if (!tableMethodsRef.current) {
      tableMethodsRef.current = {
        getTableProps: () => ({}),
        getTableBodyProps: () => ({}),
        headerGroups: [],
        prepareRow: () => { },
        page: [],
        canPreviousPage: false,
        canNextPage: false,
        pageOptions: [],
        pageCount: 0,
        gotoPage: () => { },
        nextPage: () => { },
        previousPage: () => { },
        setPageSize: () => { },
        state: { pageIndex: 0, pageSize: pageSize, globalFilter: '' },
        setGlobalFilter: () => { },
      };
    }
  } else {
    // Only update ref when tableInstance actually changes
    const instanceHash = `${stableData.length}-${stableColumns.length}-${pageSize}`;
    if (!tableMethodsRef.current || tableMethodsRef.current.instanceHash !== instanceHash) {
      const currentState = tableInstance.state || { pageIndex: 0, pageSize: pageSize, globalFilter: '' };
      tableMethodsRef.current = {
        instanceHash,
        getTableProps: tableInstance.getTableProps || (() => ({})),
        getTableBodyProps: tableInstance.getTableBodyProps || (() => ({})),
        headerGroups: tableInstance.headerGroups || [],
        prepareRow: tableInstance.prepareRow || (() => { }),
        page: tableInstance.page || [],
        canPreviousPage: tableInstance.canPreviousPage || false,
        canNextPage: tableInstance.canNextPage || false,
        pageOptions: tableInstance.pageOptions || [],
        pageCount: tableInstance.pageCount || 0,
        gotoPage: tableInstance.gotoPage || (() => { }),
        nextPage: tableInstance.nextPage || (() => { }),
        previousPage: tableInstance.previousPage || (() => { }),
        setPageSize: tableInstance.setPageSize || (() => { }),
        state: currentState,
        setGlobalFilter: tableInstance.setGlobalFilter || (() => { }),
      };
    }
  }
  
  const tableMethods = tableMethodsRef.current;

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize: currentPageSize, globalFilter },
    setGlobalFilter,
  } = tableMethods;

  // Generate a stable key based on data to force react-table reset only when data changes
  const tableKey = useMemo(() => {
    return `table-${JSON.stringify(stableData).slice(0, 100)}-${stableColumns.length}`;
  }, [stableData, stableColumns]);

  if (Platform.OS === 'web') {
    return (
      <div key={tableKey} style={{ width: '100%', overflowX: 'auto' }}>
        {/* Search/Filter Input */}
        {searchable && (
          <div style={{ marginBottom: 16 }}>
            <input
              type="text"
              value={globalFilter || ''}
              onChange={(e) => {
                e.persist?.();
                setGlobalFilter(e.target.value);
              }}
              placeholder="Search..."
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: `1px solid ${theme ? '#e2e8f0' : '#334155'}`,
                backgroundColor: theme ? '#ffffff' : '#1e293b',
                color: theme ? '#1e293b' : '#f1f5f9',
                fontSize: '14px',
                outline: 'none',
              }}
            />
          </div>
        )}

        {/* Table */}
        <table
          {...getTableProps()}
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: theme ? '#ffffff' : '#1e293b',
          }}
        >
          <thead>
            {headerGroups.map((headerGroup: any) => (
              <tr
                {...headerGroup.getHeaderGroupProps()}
                key={headerGroup.id}
                style={{
                  backgroundColor: theme ? '#f8fafc' : '#334155',
                  borderBottom: `2px solid ${theme ? '#e2e8f0' : '#475569'}`,
                }}
              >
                {headerGroup.headers.map((column: any) => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      fontWeight: '700',
                      fontSize: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      color: theme ? '#64748b' : '#94a3b8',
                      cursor: 'pointer',
                      userSelect: 'none',
                      borderRight: `1px solid ${theme ? '#e2e8f0' : '#475569'}`,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {column.render('Header')}
                      <span>
                        {column.isSorted
                          ? column.isSortedDesc
                            ? ' ↓'
                            : ' ↑'
                          : ' ↕'}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row: any) => {
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  key={row.id}
                  style={{
                    borderBottom: `1px solid ${theme ? '#e2e8f0' : '#334155'}`,
                    transition: 'background-color 0.2s',
                    cursor: onRowClick ? 'pointer' : 'default',
                  }}
                  onClick={() => onRowClick && onRowClick(row.original)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = theme ? '#f8fafc' : '#334155';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {row.cells.map((cell: any) => (
                    <td
                      {...cell.getCellProps()}
                      key={cell.column.id}
                      style={{
                        padding: '12px 16px',
                        fontSize: '14px',
                        color: theme ? '#1e293b' : '#f1f5f9',
                        borderRight: `1px solid ${theme ? '#e2e8f0' : '#334155'}`,
                      }}
                    >
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Pagination Controls */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 16,
            padding: '12px',
            backgroundColor: theme ? '#f8fafc' : '#334155',
            borderRadius: '8px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={() => gotoPage(0)}
              disabled={!canPreviousPage}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: `1px solid ${theme ? '#e2e8f0' : '#475569'}`,
                backgroundColor: !canPreviousPage
                  ? (theme ? '#f1f5f9' : '#475569')
                  : (theme ? '#ffffff' : '#1e293b'),
                color: !canPreviousPage
                  ? (theme ? '#94a3b8' : '#64748b')
                  : (theme ? '#1e293b' : '#f1f5f9'),
                cursor: !canPreviousPage ? 'not-allowed' : 'pointer',
                fontSize: '14px',
              }}
            >
              {'<<'}
            </button>
            <button
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: `1px solid ${theme ? '#e2e8f0' : '#475569'}`,
                backgroundColor: !canPreviousPage
                  ? (theme ? '#f1f5f9' : '#475569')
                  : (theme ? '#ffffff' : '#1e293b'),
                color: !canPreviousPage
                  ? (theme ? '#94a3b8' : '#64748b')
                  : (theme ? '#1e293b' : '#f1f5f9'),
                cursor: !canPreviousPage ? 'not-allowed' : 'pointer',
                fontSize: '14px',
              }}
            >
              {'<'}
            </button>
            <button
              onClick={() => nextPage()}
              disabled={!canNextPage}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: `1px solid ${theme ? '#e2e8f0' : '#475569'}`,
                backgroundColor: !canNextPage
                  ? (theme ? '#f1f5f9' : '#475569')
                  : (theme ? '#ffffff' : '#1e293b'),
                color: !canNextPage
                  ? (theme ? '#94a3b8' : '#64748b')
                  : (theme ? '#1e293b' : '#f1f5f9'),
                cursor: !canNextPage ? 'not-allowed' : 'pointer',
                fontSize: '14px',
              }}
            >
              {'>'}
            </button>
            <button
              onClick={() => gotoPage(pageCount - 1)}
              disabled={!canNextPage}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: `1px solid ${theme ? '#e2e8f0' : '#475569'}`,
                backgroundColor: !canNextPage
                  ? (theme ? '#f1f5f9' : '#475569')
                  : (theme ? '#ffffff' : '#1e293b'),
                color: !canNextPage
                  ? (theme ? '#94a3b8' : '#64748b')
                  : (theme ? '#1e293b' : '#f1f5f9'),
                cursor: !canNextPage ? 'not-allowed' : 'pointer',
                fontSize: '14px',
              }}
            >
              {'>>'}
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '14px', color: theme ? '#64748b' : '#94a3b8' }}>
              Page{' '}
              <strong>
                {pageIndex + 1} of {pageOptions.length}
              </strong>
            </span>
            <select
              value={currentPageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
              }}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: `1px solid ${theme ? '#e2e8f0' : '#475569'}`,
                backgroundColor: theme ? '#ffffff' : '#1e293b',
                color: theme ? '#1e293b' : '#f1f5f9',
                fontSize: '14px',
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              {[10, 20, 30, 50, 100].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    );
  }

  // Native fallback using IndividualDataTable
  if (dataSchema && data.length > 0) {
    return (
      <IndividualDataTable
        dataArray={data}
        clickFunction={onRowClick || (() => { })}
        dataSchema={dataSchema}
      />
    );
  }

  return (
    <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
      <Text style={{ color: theme ? '#64748b' : '#94a3b8', fontSize: 14, textAlign: 'center' }}>
        {data.length === 0 ? 'No data available' : 'Table configuration incomplete'}
      </Text>
    </View>
  );
};

// Attach contract to component
(DataTableComponent as any).uiContract = uiContract;

// Memoize the component to prevent unnecessary re-renders
export const DataTable = React.memo(DataTableComponent, (prevProps, nextProps) => {
  // Only re-render if data or columns actually changed
  const prevDataStr = JSON.stringify(prevProps.data?.data || []);
  const nextDataStr = JSON.stringify(nextProps.data?.data || []);
  const prevColumnsStr = JSON.stringify(prevProps.data?.columns || []);
  const nextColumnsStr = JSON.stringify(nextProps.data?.columns || []);
  
  return prevDataStr === nextDataStr && prevColumnsStr === nextColumnsStr &&
         prevProps.data?.pageSize === nextProps.data?.pageSize;
});

export default DataTable;

